import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { NavigationGuardContextValue } from './types';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

const NavigationGuardContext =
  createContext<NavigationGuardContextValue | null>(null);

/**
 * Connects any page that reports unsaved changes (via `setBlocked`) to any
 * navigation trigger elsewhere in the app (via `guardedNavigate`), showing a
 * confirmation dialog before an in-app navigation that would discard them.
 */
export const NavigationGuardProvider = ({ children }: PropsWithChildren) => {
  // A ref, not state: this flips on every keystroke in a dirty form, and
  // nothing here needs to re-render on that — only the dialog's open state
  // (pendingAction) needs to trigger a render.
  const isBlockedRef = useRef(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const setBlocked = useCallback((blocked: boolean) => {
    isBlockedRef.current = blocked;
  }, []);

  const guardedNavigate = useCallback((action: () => void) => {
    if (isBlockedRef.current) {
      setPendingAction(() => action);
    } else {
      action();
    }
  }, []);

  const onCancel = () => setPendingAction(null);

  const onConfirm = () => {
    const action = pendingAction;

    setPendingAction(null);
    isBlockedRef.current = false;
    action?.();
  };

  return (
    <NavigationGuardContext.Provider value={{ setBlocked, guardedNavigate }}>
      {children}
      <UnsavedChangesDialog
        open={pendingAction !== null}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    </NavigationGuardContext.Provider>
  );
};

/** Access the navigation guard from a page (`setBlocked`) or a nav trigger (`guardedNavigate`). */
export const useNavigationGuard = () => {
  const context = useContext(NavigationGuardContext);

  if (!context) {
    throw new Error(
      'useNavigationGuard must be used within a NavigationGuardProvider',
    );
  }

  return context;
};
